// Mini App "Members" page. Owns data fetching (TanStack Query hooks from
// entities/community + entities/member) and composes the
// widgets/members-panel. The active-community Zustand store is shared
// across pages; the chip selection lives in the filter-members feature
// store and is reset on unmount so the next page-open starts clean.
//
// Per the FSD layer graph, pages may import from widgets, features,
// entities, and shared. They may NOT import from another page.

import { useEffect, useMemo } from "react";

import {
  CommunitySelector,
  selectActiveCommunityId,
  useActiveCommunityStore,
  useCommunitiesQuery,
} from "@entities/community";
import { useMembersQuery } from "@entities/member";
import {
  selectSelectedStages,
  useFilterMembersStore,
} from "@features/filter-members";
import {
  MEMBERS_STRINGS,
  MembersEmptyState,
  MembersErrorState,
  MembersPanel,
  MembersSkeleton,
} from "@widgets/members-panel";

export function MembersPage() {
  const activeCommunityId = useActiveCommunityStore(selectActiveCommunityId);
  const setActiveCommunityId = useActiveCommunityStore(
    (s) => s.setActiveCommunityId,
  );
  const selectedStages = useFilterMembersStore(selectSelectedStages);
  const resetFilter = useFilterMembersStore((s) => s.reset);

  const communitiesQuery = useCommunitiesQuery();

  // Auto-select the first community as soon as the list resolves and the
  // store hasn't been seeded yet — same fallback as OverviewPage.
  useEffect(() => {
    if (activeCommunityId !== null) return;
    const first = communitiesQuery.data?.[0];
    if (first) setActiveCommunityId(first.id);
  }, [activeCommunityId, communitiesQuery.data, setActiveCommunityId]);

  // Reset chip selection when the page unmounts so re-opening starts clean.
  useEffect(() => {
    return () => resetFilter();
  }, [resetFilter]);

  const membersQuery = useMembersQuery(activeCommunityId, selectedStages);

  // Flatten infinite-query pages into a single list. Memoized so MembersPanel
  // doesn't re-key its <li>s on unrelated re-renders.
  const allItems = useMemo(
    () => membersQuery.data?.pages.flatMap((p) => p.items) ?? [],
    [membersQuery.data],
  );
  const counts =
    membersQuery.data?.pages[0]?.counts ?? {
      NEW: 0,
      ACTIVE: 0,
      ENGAGED: 0,
      DORMANT: 0,
      AT_RISK: 0,
      CHURNED: 0,
    };
  const upgradeRequired =
    membersQuery.data?.pages[0]?.upgradeRequired ?? false;

  // Header is always rendered so the selector doesn't pop in late.
  const header = (
    <header className="flex items-center justify-between gap-3 pb-2">
      <h1 className="text-lg font-semibold">{MEMBERS_STRINGS.pageTitle}</h1>
      <div className="flex items-center gap-2">
        {communitiesQuery.data && communitiesQuery.data.length > 0 ? (
          <CommunitySelector
            communities={communitiesQuery.data}
            activeCommunityId={activeCommunityId}
            onSelect={setActiveCommunityId}
            label="Active community"
          />
        ) : null}
      </div>
    </header>
  );

  // 1) communities list errored — page-wide error state.
  if (communitiesQuery.isError) {
    return (
      <main className="container flex min-h-screen flex-col gap-4 py-4">
        {header}
        <MembersErrorState
          title={MEMBERS_STRINGS.errorTitle}
          body={
            communitiesQuery.error?.message ?? MEMBERS_STRINGS.errorBodyDefault
          }
          retryLabel={MEMBERS_STRINGS.retry}
          onRetry={() => void communitiesQuery.refetch()}
        />
      </main>
    );
  }

  // 2) communities list resolved → user has zero communities → onboarding.
  if (communitiesQuery.data && communitiesQuery.data.length === 0) {
    return (
      <main className="container flex min-h-screen flex-col gap-4 py-4">
        {header}
        <MembersEmptyState
          title={MEMBERS_STRINGS.noCommunity.title}
          body={MEMBERS_STRINGS.noCommunity.body}
        />
      </main>
    );
  }

  // 3) members query errored — surface a single retry CTA.
  if (membersQuery.isError) {
    return (
      <main className="container flex min-h-screen flex-col gap-4 py-4">
        {header}
        <MembersErrorState
          title={MEMBERS_STRINGS.errorTitle}
          body={
            membersQuery.error?.message ?? MEMBERS_STRINGS.errorBodyDefault
          }
          retryLabel={MEMBERS_STRINGS.retry}
          onRetry={() => void membersQuery.refetch()}
        />
      </main>
    );
  }

  // 4) Loading — render the skeleton beneath the header so layout doesn't
  // shift when the data lands.
  if (
    !communitiesQuery.data ||
    !membersQuery.data ||
    activeCommunityId === null
  ) {
    return (
      <main className="container flex min-h-screen flex-col gap-4 py-4">
        {header}
        <MembersSkeleton loadingLabel={MEMBERS_STRINGS.loading} />
      </main>
    );
  }

  // 5) Happy path.
  return (
    <main className="container flex min-h-screen flex-col gap-4 py-4">
      {header}
      <MembersPanel
        upgradeRequired={upgradeRequired}
        items={allItems}
        counts={counts}
        selectedStages={selectedStages}
        isFetchingNextPage={membersQuery.isFetchingNextPage}
        hasNextPage={membersQuery.hasNextPage ?? false}
        onLoadMore={() => void membersQuery.fetchNextPage()}
      />
    </main>
  );
}
